// Imports
import { darken, lighten } from 'polished';
import React from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components/macro';
import FreshStyle from './fresh-style';
import ModernStyle from './modern-style';
import NatureStyle from './nature-style';
import PaperStyle from './paper-style';

/* A function that returns a theme object for the specified template ID  */
const generateThemeProps = (id: number) => {
	// Define each color variant
	const colors: Record<number, string> = {
		// Fresh templates
		1: '#1b6baf', // Blue
		2: '#1b6baf', // Blue
		3: '#70b021', // Green
		4: '#70b021', // Green
		5: '#a17435', // Brown
		6: '#a17435', // Brown
		7: '#a12f2f', // Red
		8: '#a12f2f', // Red
		9: '#32a687', // Teal
		10: '#32a687', // Teal
		11: '#6349ba', // Purple
		12: '#6349ba', // Purple
		// Modern templates
		13: '#1b6baf', // Blue
		14: '#1b6baf', // Blue
		15: '#345c8a', // Dark blue
		16: '#345c8a', // Dark blue
		17: '#70b021', // Green
		18: '#70b021', // Green
		19: '#4e6b2b', // Dark green
		20: '#4e6b2b', // Dark green
		21: '#80532b', // Brown
		22: '#80532b', // Brown
		23: '#932e2e', // Red
		24: '#932e2e', // Red
		25: '#bf7a02', // Orange
		26: '#bf7a02', // Orange
		// Nature templates
		27: '#932e2e', // Autumn
		28: '#932e2e', // Autumn
		29: '#888888', // Pebbles
		30: '#888888', // Pebbles
		// Paper templates
		31: '#000', // White
		32: '#000', // White
	};
	
	
	// Initialize
	const baseColor = colors[id] || colors[1];
	
	
	// Return
	return {
		baseColor: baseColor,
		fontFamily: "'Trebuchet MS', 'Arial', 'Helvetica', 'sans-serif'",
		contentLinkColor: lighten(0.1, baseColor),
		contentLinkHoverColor: darken(0.05, baseColor),
		...(id % 2 === 0 ? { isFlipped: true } : {}),
		...(id >= 1 && id <= 12 ? { isFreshStyle: true } : {}),
		...(id === 3 || id === 4 ? { customResellerPhraseLighten: 0.05, contentLinkColor: lighten(0.05, baseColor) } : {}),
		...(id === 9 || id === 10 ? { customResellerPhraseLighten: 0.05 } : {}),
		...(id >= 13 && id <= 26 ? { isModernStyle: true, isModernOrNatureStyle: true } : {}),
		...(id === 17 || id === 18 ? { contentLinkColor: baseColor } : {}),
		...(id === 25 || id === 26
			? { contentLinkColor: darken(0.05, baseColor), contentLinkHoverColor: darken(0.15, baseColor) }
			: {}),
		...(id >= 27 && id <= 30 ? { isNatureStyle: true, isModernOrNatureStyle: true } : {}),
		...(id >= 27 && id <= 28 ? { isAutumn: true } : {}),
		...(id >= 29 && id <= 30 ? { isPebbles: true } : {}),
		...(id === 31 || id === 32 ? { isPaper: true, customResellerPhraseLighten: 0.4 } : {}),
		...([3, 4, 17, 18, 19, 20, 25, 26].includes(id) ? { bottomInfoLinkIsBaseColor: true } : {}),
		...([17, 18, 19, 20, 25, 26].includes(id) ? { customBottomInfoBackgroundLighten: 0.45 } : {}),
	};
};


// Export the type for re-use
export type GeneratedThemeProps = ReturnType<typeof generateThemeProps>;


// Override form submit button styles for Storybook
//  -> Otherwise, screenshots will have the incorrect default primary color
const StorybookFormStyle = createGlobalStyle<{ theme: GeneratedThemeProps }>`
  form {
		button[type='submit'] {
			background-color: ${(props) => props.theme.baseColor};
			
			&:hover,
			&:focus {
				background-color: ${(props) => darken(0.05, props.theme.baseColor)} !important;
			}
		}
	}
`;


// Define the accepted props
interface Props extends React.PropsWithChildren {
	id: number;
}


// Functional component
const TemplateStyle: React.FC<Props> = (props) => (
	<ThemeProvider theme={generateThemeProps(props.id)}>
		{(window as typeof window & { STORYBOOK?: true }).STORYBOOK && <StorybookFormStyle />}
		{props.id >= 1 && props.id <= 12 ? <FreshStyle /> : null}
		{props.id >= 13 && props.id <= 26 ? <ModernStyle /> : null}
		{props.id >= 27 && props.id <= 30 ? <NatureStyle /> : null}
		{props.id === 31 || props.id === 32 ? <PaperStyle /> : null}
		{props.children}
	</ThemeProvider>
);

export default TemplateStyle;
